import {
  Component,
  OnInit,
  AfterViewInit,
  ChangeDetectorRef,
  ViewChild,
  ElementRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { RouterModule, Router, ActivatedRoute } from '@angular/router';
import { DatePickerModule } from 'primeng/datepicker';
import { MultiSelectModule } from 'primeng/multiselect';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { LettersService } from '../../../shared/services/letters.service';
import { LetterDetails } from '../../../shared/models/letters.model';
import { AuthService } from '../../../core/services/auth.service';
import { UserProfile, UserRole } from '../../../shared/models/auth.model';
import { SideNavComponent } from '../../../shared/components/layout/side-nav/side-nav.component';
import { PageHeaderComponent } from '../../../shared/components/layout/page-header/page-header.component';

@Component({
  selector: 'app-letters-upload',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    DatePickerModule,
    MultiSelectModule,
    SelectModule,
    ToastModule,
    SideNavComponent,
    PageHeaderComponent,
  ],
  providers: [MessageService],
  templateUrl: './letters-upload.component.html',
  styleUrl: './letters-upload.component.scss',
})
export class LettersUploadComponent implements OnInit, AfterViewInit {
  @ViewChild('dueDatePicker') dueDatePicker: any;

  // Layout/UI state
  isSidebarOpen: boolean = false;
  isPageLoading: boolean = false;

  // Form
  uploadForm: FormGroup;

  // File upload
  selectedFile: File | null = null;
  isDragOver = false;
  fileSizeError: string = '';
  maxFileSize = 15 * 1024 * 1024; // 15MB in bytes

  // Edit mode
  isEditMode: boolean = false;
  editData: LetterDetails | null = null;
  documentId: string = '';
  uploadedFileName: string = '';
  uploadedFileUrl: string = '';

  // Date properties
  minDate: Date = (() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
  })();
  // Use this for the template to allow older dates in edit mode
  computedMinDate: Date | null = this.minDate;

  // Dropdown options
  assignedUsers: { id: number; name: string }[] = [];
  isLoadingUsers: boolean = true;
  usersLoadError: boolean = false;

  sources: { id: number; name: string }[] = [];
  isLoadingSources: boolean = true;
  sourcesLoadError: boolean = false;

  // User profile
  userProfile: UserProfile | null = null;

  // Date picker sync state (used by edit mode prefill flow)
  defaultDate: Date | null = null;
  pendingDateValue: Date | null = null;
  isSettingInitialValue: boolean = false;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute,
    private lettersService: LettersService,
    private messageService: MessageService,
    private authService: AuthService,
  ) {
    this.uploadForm = this.fb.group({
      documentName: [
        '',
        [
          Validators.required,
          Validators.minLength(3),
          Validators.maxLength(100),
          Validators.pattern(/^[a-zA-Z0-9\s\-_.,()]+$/),
        ],
      ],
      dueDate: ['', [Validators.required]],
      assignedUsers: [[], [Validators.required]],
      source: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    // console.log('=== LETTERS UPLOAD COMPONENT INITIALIZED ===');
    this.checkUserRole();
    this.loadAssignableUsers();
    this.loadActiveSources();
    this.checkEditMode();
    this.setupFormValueChanges();
    // console.log('Initial form state:', this.uploadForm.value);
    // console.log('============================================');
  }

  ngAfterViewInit(): void {
    if (this.pendingDateValue) {
      this.uploadForm.get('dueDate')?.setValue(this.pendingDateValue);
      this.triggerDatePickerUpdate();
      this.pendingDateValue = null;
    }
  }

  /**
   * Check user role and set user profile
   */
  private checkUserRole(): void {
    const currentUser = this.authService.getCurrentUser();

    // Set userProfile from authenticated user
    if (currentUser) {
      this.userProfile = {
        id: currentUser.email,
        name: currentUser.name,
        email: currentUser.email,
        role: currentUser.role,
        avatar: 'assets/images/logo.png',
      };
    }
  }

  /**
   * Setup form value changes monitoring
   */
  private setupFormValueChanges(): void {
    // Monitor assignedUsers form control changes
    this.uploadForm
      .get('assignedUsers')
      ?.valueChanges.subscribe((selectedUsers) => {
        if (Array.isArray(selectedUsers)) {
          // Extract just the IDs
          const selectedIds = selectedUsers
            .map((user) => user?.id)
            .filter((id) => id !== undefined);
        }
      });
  }

  /**
   * Load assignable users from the API
   */
  private loadAssignableUsers(): void {
    this.isLoadingUsers = true;
    this.usersLoadError = false;

    this.lettersService.getAssignableUsers().subscribe({
      next: (users: any) => {
        this.assignedUsers = users || [];
        this.isLoadingUsers = false;
        this.usersLoadError = false;
      },
      error: (error: any) => {
        this.isLoadingUsers = false;
        // console.log('Using fallback users:', this.assignedUsers);

        // Show error toast
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load assignable users',
        });
      },
    });
  }

  /**
   * Load active sources from the API
   */
  private loadActiveSources(): void {
    this.isLoadingSources = true;
    this.sourcesLoadError = false;

    this.lettersService.getActiveSources().subscribe({
      next: (sources: any) => {
        this.sources = sources || [];
        this.isLoadingSources = false;
        this.sourcesLoadError = false;
      },
      error: (error: any) => {
        this.isLoadingSources = false;
        // console.log('Using fallback sources:', this.sources);

        // Show error toast
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to load sources',
        });
      },
    });
  }

  // Getter to check if assigned users are selected
  get hasAssignedUsers(): boolean {
    const assignedUsers = this.uploadForm.get('assignedUsers')?.value;
    return Array.isArray(assignedUsers) && assignedUsers.length > 0;
  }

  // Helper method to check if a field has errors
  hasFieldError(fieldName: string): boolean {
    const field = this.uploadForm.get(fieldName);
    return !!(field && field.invalid && field.touched);
  }

  // Helper method to get field error message
  getFieldError(fieldName: string): string {
    const field = this.uploadForm.get(fieldName);
    if (!field || !field.errors || !field.touched) {
      return '';
    }

    const errors = field.errors;

    if (errors['required']) {
      return this.getRequiredErrorMessage(fieldName);
    }
    if (errors['minlength']) {
      return `${this.getFieldLabel(fieldName)} must be at least ${
        errors['minlength'].requiredLength
      } characters long.`;
    }
    if (errors['maxlength']) {
      return `${this.getFieldLabel(fieldName)} must not exceed ${
        errors['maxlength'].requiredLength
      } characters.`;
    }
    if (errors['pattern']) {
      return `${this.getFieldLabel(
        fieldName,
      )} contains invalid characters. Only letters, numbers, spaces, and basic punctuation are allowed.`;
    }

    return `${this.getFieldLabel(fieldName)} is invalid.`;
  }

  // Helper method to get required error message
  private getRequiredErrorMessage(fieldName: string): string {
    const fieldLabels: { [key: string]: string } = {
      documentName: 'Document name is required',
      dueDate: 'Due date is required',
      assignedUsers: 'Please select at least one assigned user',
      source: 'Please select a source',
    };
    return (
      fieldLabels[fieldName] || `${this.getFieldLabel(fieldName)} is required`
    );
  }

  // Helper method to get field label
  private getFieldLabel(fieldName: string): string {
    const fieldLabels: { [key: string]: string } = {
      documentName: 'Document name',
      dueDate: 'Due date',
      assignedUsers: 'Assigned users',
      source: 'Source',
    };
    return fieldLabels[fieldName] || fieldName;
  }

  private checkEditMode(): void {
    // Check if we're in edit mode based on the current route
    this.isEditMode = this.router.url.includes('/edit');

    if (this.isEditMode) {
      // Always prevent selecting past dates, even in edit mode
      this.computedMinDate = this.minDate;
      this.loadEditData();
    }
  }

  private loadEditData(): void {
    this.route.queryParams.subscribe((params) => {
      if (params['id']) {
        this.documentId = params['id'];
        this.lettersService.getLetterById(this.documentId).subscribe({
          next: (letterDetails) => {
            this.editData = letterDetails;
            // Wait for dropdowns to be loaded before prefilling
            this.waitForDropdownsAndPrefill(letterDetails);
          },
          error: (error) => {
            console.error('Error loading letter details for edit:', error);

            // Show error toast
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to load letter details for editing',
            });
          },
        });
      }
    });
  }

  private waitForDropdownsAndPrefill(letterDetails: any): void {
    // Check if dropdowns are already loaded
    if (!this.isLoadingUsers && !this.isLoadingSources) {
      this.prefillForm(letterDetails);
      return;
    }

    // Wait for dropdowns to load with a timeout
    const maxWaitTime = 5000; // 5 seconds
    const checkInterval = 100; // Check every 100ms
    let elapsedTime = 0;

    const checkDropdowns = () => {
      if (!this.isLoadingUsers && !this.isLoadingSources) {
        this.prefillForm(letterDetails);
      } else if (elapsedTime < maxWaitTime) {
        elapsedTime += checkInterval;
        setTimeout(checkDropdowns, checkInterval);
      } else {
        this.prefillForm(letterDetails);
      }
    };

    checkDropdowns();
  }

  private prefillForm(letterDetails: any): void {
    // Store uploaded file information for edit mode
    if (letterDetails.uploadedFileName) {
      this.uploadedFileName = letterDetails.uploadedFileName;
    }

    // Find the matching source by name
    const matchingSource = this.sources.find(
      (s) => s.name === letterDetails.source,
    );

    // Handle assigned users from the API response array
    let selectedUsers: any[] = [];
    if (
      letterDetails.assignedUsers &&
      Array.isArray(letterDetails.assignedUsers)
    ) {
      // Only include users that exist in both the workflow API and assignable users API
      selectedUsers = letterDetails.assignedUsers
        .map((apiUser: any) => {
          // Find matching user in our assignedUsers list by userId
          const matchingUser = this.assignedUsers.find(
            (user) => user.id === apiUser.userId,
          );
          return matchingUser; // Return only if found, otherwise undefined
        })
        .filter((user: any) => user !== undefined); // Remove undefined entries
    } else if (letterDetails.assignedTo) {
      // Fallback to old format (comma-separated string)
      const userNames = letterDetails.assignedTo
        .split(',')
        .map((name: string) => name.trim());
      selectedUsers = this.assignedUsers.filter((user) =>
        userNames.some((name: string) => user.name === name),
      );
    }

    // Parse due date - handle both ISO string and DD-MM-YYYY format
    let parsedDueDate: Date | null = null;
    if (letterDetails.dueDate) {
      try {
        if (
          letterDetails.dueDate.includes('T') ||
          letterDetails.dueDate.includes('-')
        ) {
          // ISO string format (e.g., "2025-10-06T18:30:00")
          parsedDueDate = new Date(letterDetails.dueDate);
        } else {
          // DD-MM-YYYY format
          parsedDueDate = this.parseDate(letterDetails.dueDate);
        }

        // Validate the parsed date
        if (isNaN(parsedDueDate.getTime())) {
          parsedDueDate = null;
        }
      } catch (error) {
        parsedDueDate = null;
      }
    }

    // Set form values
    this.uploadForm.patchValue({
      documentName: letterDetails.documentName,
      source: matchingSource ? matchingSource.id : '',
      assignedUsers: selectedUsers, // Pass the full objects for multiselect
    });

    // Set due date separately to ensure proper handling
    if (parsedDueDate) {
      // Keep the existing date as-is, even if it's in the past
      // Set computedMinDate to null if existing date is in the past to allow displaying it
      // Otherwise use today as minDate to prevent selecting new past dates
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const existingDate = new Date(parsedDueDate);
      existingDate.setHours(0, 0, 0, 0);

      // If existing date is in the past, set computedMinDate to null to allow displaying it
      // We'll enforce today as minDate when user tries to change it via a change listener
      // For today's date or future dates, use minDate (which is today at midnight)
      if (existingDate < today) {
        this.computedMinDate = null;
      } else {
        // Use minDate which is already set to today at midnight
        // This ensures today's date can be displayed properly
        this.computedMinDate = this.minDate;
      }

      // Ensure parsedDueDate also has time set to midnight for consistency
      parsedDueDate.setHours(0, 0, 0, 0);

      // Set the default date for the date picker
      this.defaultDate = parsedDueDate;

      // Store the date value to set after view init
      this.pendingDateValue = parsedDueDate;

      // Set flag to prevent validation on initial load
      this.isSettingInitialValue = true;

      // Try to set it immediately as well
      this.uploadForm.get('dueDate')?.setValue(parsedDueDate);

      // Reset flag after a short delay
      setTimeout(() => {
        this.isSettingInitialValue = false;
      }, 100);

      // Also try patching the entire form to trigger a complete update
      setTimeout(() => {
        this.isSettingInitialValue = true;
        this.uploadForm.patchValue({
          dueDate: parsedDueDate,
        });
        setTimeout(() => {
          this.isSettingInitialValue = false;
        }, 100);

        // Try to trigger date picker update
        this.triggerDatePickerUpdate();
      }, 50);
    } else {
      this.defaultDate = null;
      this.uploadForm.get('dueDate')?.setValue(null);
    }

    // Force form update and mark as touched to ensure UI updates
    this.uploadForm.get('dueDate')?.markAsTouched();
    this.uploadForm.get('dueDate')?.updateValueAndValidity();

    // Try to trigger change detection for the date picker
    setTimeout(() => {
      // Try setting the value again after a short delay
      if (parsedDueDate) {
        this.isSettingInitialValue = true;
        this.uploadForm.get('dueDate')?.setValue(parsedDueDate);
        setTimeout(() => {
          this.isSettingInitialValue = false;
        }, 100);

        // Additional attempts to ensure UI updates
        setTimeout(() => {
          this.isSettingInitialValue = true;
          this.uploadForm.get('dueDate')?.setValue(parsedDueDate);
          setTimeout(() => {
            this.isSettingInitialValue = false;
          }, 100);

          // Try to trigger the date picker's internal update
          this.triggerDatePickerUpdate();
        }, 200);
      }
    }, 100);
  }

  private parseDate(dateString: string): Date {
    // Parse date string in DD-MM-YYYY format to Date object
    const [day, month, year] = dateString.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private triggerDatePickerUpdate(): void {
    try {
      if (this.dueDatePicker) {
        // Try to trigger the date picker's internal update mechanism
        if (this.dueDatePicker.updateModel) {
          this.dueDatePicker.updateModel();
        }
        if (this.dueDatePicker.updateInputfield) {
          this.dueDatePicker.updateInputfield();
        }
        // Try to set the value directly on the date picker
        if (
          this.dueDatePicker.value !== this.uploadForm.get('dueDate')?.value
        ) {
          this.dueDatePicker.value = this.uploadForm.get('dueDate')?.value;
        }
      }
    } catch (error) {
      // Handle error silently
    }
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.fileSizeError = '';
      if (this.isValidFile(file)) {
        this.selectedFile = file;
      }
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      const file = files[0];
      this.fileSizeError = '';
      if (this.isValidFile(file)) {
        this.selectedFile = file;
      }
    }
  }

  isValidFile(file: File): boolean {
    // Check file type - allow PDF and DOCX files
    const allowedTypes = [
      'application/pdf',
      '.doc',
      '.docx',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // DOCX
    ];

    if (!allowedTypes.includes(file.type)) {
      this.fileSizeError = 'Only PDF and DOC files are allowed.';
      return false;
    }

    // Check file size (15MB limit)
    if (file.size > this.maxFileSize) {
      this.fileSizeError = `File size exceeds the maximum limit of 15MB. Current file size: ${this.formatFileSize(
        file.size,
      )}`;
      return false;
    }

    return true;
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  onSave(): void {
    if (this.uploadForm.valid && (this.selectedFile || this.isEditMode)) {
      // Create FormData for multipart/form-data request
      const formData = new FormData();

      // Add form fields
      formData.append(
        'DocumentName',
        this.uploadForm.get('documentName')?.value,
      );

      // For update operations, add the document ID to the payload
      if (this.isEditMode && this.documentId) {
        formData.append('Id', this.documentId);
      }

      // Use the source ID from the form (from activesources API)
      const sourceId = this.uploadForm.get('source')?.value.toString();
      formData.append('SourceId', sourceId);

      // Format due date as ISO string at UTC midnight to avoid timezone shifts
      const dueDate = this.uploadForm.get('dueDate')?.value;
      if (dueDate) {
        const date = new Date(dueDate);
        // Create date at UTC midnight to avoid timezone conversion issues
        const utcDate = new Date(
          Date.UTC(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
            0,
            0,
            0,
            0,
          ),
        );
        formData.append('DueDate', utcDate.toISOString());
      }

      // Add assigned user IDs as separate fields (server expects this format)
      const assignedUsers = this.uploadForm.get('assignedUsers')?.value;

      if (assignedUsers && Array.isArray(assignedUsers)) {
        assignedUsers.forEach((user: any, index: number) => {
          formData.append('AssignedUserIds', user.id.toString());
        });
      }

      // Add file if present
      if (this.selectedFile) {
        formData.append('File', this.selectedFile);
      }

      // Call the API
      const api$ =
        this.isEditMode && this.documentId
          ? this.lettersService.updateLetterMultipart(this.documentId, formData)
          : this.lettersService.createLetter(formData);

      api$.subscribe({
        next: (response: any) => {
          console.log('Letter created successfully:', response);

          // Show success toast
          this.messageService.add({
            severity: 'success',
            summary: 'Success',
            detail: this.isEditMode
              ? 'Letter updated successfully'
              : 'Letter added successfully',
          });

          // Navigate back appropriately
          if (this.isEditMode && this.documentId) {
            // Show success message
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Letter updated successfully',
              life: 5000,
            });
            // Navigate to details page with success parameter for updates
            // Delay navigation to allow message to be visible
            setTimeout(() => {
              this.router.navigate(['/letters/details'], {
                queryParams: {
                  id: this.documentId,
                  updated: 'true',
                },
              });
            }, 1500);
          } else {
            // For new letters, check if response contains ID and navigate to details page
            const letterId = response?.id || response?.data?.id;
            if (letterId) {
              // Navigate to details page with success parameter for new letters
              this.router.navigate(['/letters/details'], {
                queryParams: {
                  id: letterId,
                  saved: 'true',
                },
              });
            } else {
              // Fallback: show toast and navigate to list if no ID in response
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Letter added successfully',
                life: 3000,
              });
              setTimeout(() => {
                this.router.navigate(['/letters']);
              }, 2000);
            }
          }
        },
        error: (error: any) => {
          console.error('Error creating letter:', error);
          console.error('Error status:', error.status);
          console.error('Error message:', error.message);
          console.error('Error details:', error.error);

          // Show error toast
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Something went wrong',
          });
        },
      });
    } else {
      // Mark all fields as touched to show validation errors
      Object.keys(this.uploadForm.controls).forEach((key) => {
        const control = this.uploadForm.get(key);
        control?.markAsTouched();
      });
    }
  }

  onCancel(): void {
    if (this.isEditMode && this.documentId) {
      // Navigate back to letter details page with the current document info
      this.router.navigate(['/letters/details'], {
        queryParams: {
          id: this.documentId,
        },
      });
    } else {
      // Navigate to letters list
      this.router.navigate(['/letters']);
    }
  }

  onSidebarToggle(): void {
    this.isSidebarOpen = !this.isSidebarOpen;
  }

  onSidebarClose(): void {
    this.isSidebarOpen = false;
  }

  getFileName(): string {
    return this.selectedFile ? this.selectedFile.name : '';
  }

  getUploadedFileName(): string {
    return this.uploadedFileName || '';
  }

  getFileExtension(): string {
    if (this.uploadedFileName) {
      const lastDotIndex = this.uploadedFileName.lastIndexOf('.');
      return lastDotIndex !== -1
        ? this.uploadedFileName.substring(lastDotIndex + 1).toLowerCase()
        : '';
    }
    return '';
  }

  getFileIcon(): string {
    const extension = this.getFileExtension();
    switch (extension) {
      case 'pdf':
        return 'pi pi-file-pdf';
      case 'doc':
      case 'docx':
        return 'pi pi-file-word';
      case 'xls':
      case 'xlsx':
        return 'pi pi-file-excel';
      case 'ppt':
      case 'pptx':
        return 'pi pi-file-powerpoint';
      case 'txt':
        return 'pi pi-file';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return 'pi pi-image';
      default:
        return 'pi pi-file';
    }
  }

  downloadUploadedFile(): void {
    if (!this.documentId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No document ID available for download.',
      });
      return;
    }

    // Use the download API endpoint like document-viewer-modal does
    this.lettersService.downloadLetterDocument(this.documentId).subscribe({
      next: (response: { blob: Blob; filename: string; type: string }) => {
        // Create download link from blob
        const url = window.URL.createObjectURL(response.blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = response.filename;

        // Trigger download
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Document downloaded successfully.',
        });
      },
      error: (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to download document. Please try again.',
        });
      },
    });
  }

  removeFile(): void {
    this.selectedFile = null;
    this.fileSizeError = '';
    // Reset the file input
    const fileInput = document.querySelector(
      'input[type="file"]',
    ) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }
  }

  /**
   * Retry loading assignable users
   */
  retryLoadUsers(): void {
    this.loadAssignableUsers();
  }

  /**
   * Retry loading active sources
   */
  retryLoadSources(): void {
    this.loadActiveSources();
  }

  /**
   * Refresh all data (users and sources)
   */
  refreshAllData(): void {
    this.loadAssignableUsers();
    this.loadActiveSources();
  }

  /**
   * Debug method to log current form state
   */
  logFormState(): void {
    // Debug method - no logging in production
  }
}
